import React, {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Icon} from '@iconify/react';
import {roomSelector} from '../../../../../store/selectors/roomRunFile.selectors';
import ViewerWaitingLabel from './ViewerWaitingLabel';
import Lobby1 from '../../../../../../assets/images/content/lobby_transparent_1.png';
import Lobby2 from '../../../../../../assets/images/content/lobby_transparent_2.png';
import Lobby3 from '../../../../../../assets/images/content/lobby_transparent_3.png';

function ViewerView(): JSX.Element {
    const room = useSelector(roomSelector);
    const {t} = useTranslation();

    const [messageIndex, setMessageIndex] = useState(0);
    const [imageIndex, setImageIndex] = useState(0);

    const images = [Lobby1, Lobby2, Lobby3];

    const messages = [
        {
            text: 'lobby.viewer.navigateWithLeftAndRightArrowOnKeyboardOrByClickingOnTheSideArrowsNextToTheSlide',
            icon: 'solar:keyboard-linear',
        },
        {
            text: 'lobby.viewer.interactiveElementsCanBeAnsweredByClickingOnThem',
            icon: 'solar:cursor-linear',
        },
        {
            text: 'lobby.viewer.gatherPointsByAnsweringQuestionsCorrectly',
            icon: 'solar:star-linear',
        },
        {
            text: 'lobby.viewer.youCanSeeYourPointsInTheHeader',
            icon: 'solar:cup-star-linear',
        },
        {
            text: 'lobby.viewer.someQuestionNeedToBeAnsweredBeforeMovingToNextSlide',
            icon: 'solar:question-circle-linear',
        },
        {
            text: 'lobby.viewer.imageMarkupElementRequiresYouToSelectCorrectAreaOnTheImage',
            icon: 'solar:pen-linear',
        },
        {
            text: 'lobby.viewer.sequenceElementRequiresYouToArrangeElementsInCorrectOrder',
            icon: 'solar:list-arrow-down-minimalistic-linear',
        },
        {
            text: 'lobby.viewer.multiselectElementRequiresYouToSelectAllCorrectAnswers',
            icon: 'solar:list-check-bold',
        },
        {
            text: 'lobby.viewer.changeTheLanguageInTheHeader',
            icon: 'solar:earth-linear',
        },
        {
            text: 'lobby.viewer.matchPairElementRequiresYouToMatchPairsOfElements',
            icon: 'solar:widget-2-linear',
        },
        {
            text: 'lobby.viewer.likertScaleElementDoesNotHaveCorrectAnswers',
            icon: 'solar:tuning-2-linear',
        },
    ] as const;

    const shuffledIndices = useMemo(() => {
        const indices = [...Array(messages.length).keys()];
        for (let i = indices.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [indices[i], indices[j]] = [indices[j], indices[i]];
        }
        return indices;
    }, [messages.length]);

    useEffect(() => {
        const interval = setInterval(() => {
            setMessageIndex((prevIndex) => (prevIndex + 1) % shuffledIndices.length);
        }, 7000);

        return () => clearInterval(interval);
    }, [shuffledIndices.length]);

    useEffect(() => {
        const interval = setInterval(() => {
            setImageIndex((prevIndex) => (prevIndex + 1) % images.length);
        }, 7000);

        return () => clearInterval(interval);
    }, [images.length]);

    const currentIndex = shuffledIndices[messageIndex];

    return (
        <div className="p-lobby__viewerWrapper">
            <div className="p-lobby__viewerHeader">
                <div className="p-lobby__viewerHeader_title">{room.presentation?.name}</div>
                <div className="p-lobby__viewerHeader_description">
                    {room.presentation?.description || t('lobby.noDescriptionProvided')}
                </div>
            </div>

            <div className="p-lobby__viewerContent">
                <img className="p-lobby__viewerContent_image" src={images[imageIndex]} alt="lobby-background" />
            </div>

            <div className="p-lobby__viewerFooter">
                <div className="p-lobby__viewerProgress">
                    <div className="p-lobby__viewerProgress_waitingForPresenter">
                        <ViewerWaitingLabel />
                    </div>
                    <div className="p-lobby__viewerProgress_progressBarWrapper">
                        <div className="p-lobby__viewerProgress_progressBar" />
                    </div>
                </div>
                <div className="p-lobby__viewerInfo" key={currentIndex}>
                    <Icon className="p-lobby__viewerInfo_icon" icon={messages[currentIndex].icon} />
                    <div className="p-lobby__viewerInfo_text">{t(messages[currentIndex].text)}</div>
                </div>
            </div>
        </div>
    );
}

export default ViewerView;
